import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getToken } from '../auth/UserService';
import MainLoader from '../components/shared/MainLoader';
import ModalImportLoading from '../components/shared/ModalImportLoading';
import { DEFAULTTHEME, THEME_COLORS } from '../constants/theme';
import useBoolean from '../hooks/use-boolean';
import { FormikTheme, MainThemeListSingleItem } from '../interfaces/theme';
import PropertyAssetFeatureService from '../services/PropertyAssetFeature';
import ThemeService from '../services/ThemeService';
import ThemeState from './state/ThemeState';

interface Props {
    children: React.ReactNode;
}

const ThemeContext: FunctionComponent<Props> = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState(DEFAULTTHEME),
        [previewTheme, setPreviewTheme] = useState({
            colors: THEME_COLORS,
            logo: DEFAULTTHEME.logo,
            lightLogo: DEFAULTTHEME.lightLogo,
            loader: DEFAULTTHEME.loader,
        }),
        [companyIdToFilterBy, setCompanyIdToFilterBy] = useState(''),
        [isEditMode, isEditModeActions] = useBoolean(),
        [isThemeListLoading, isThemeListLoadingActions] = useBoolean(),
        { t } = useTranslation(['f', 'b']),
        [themeList, setThemeList] = useState<Array<MainThemeListSingleItem>>([]),
        [selectedThemeForSaveAndEdit, setSelectedThemeForSaveAndEdit] = useState<string>(DEFAULTTHEME.id),
        [isThemePublishing, isThemePublishingActions] = useBoolean(),
        [applayThemeLoader, applayThemeLoaderActions] = useBoolean(),
        [mainThemeId, setMainThemeId] = useState<string>(DEFAULTTHEME.id);
    const isDev = process.env.NODE_ENV === 'development';

    const getDevFallbackThemes = useCallback((): MainThemeListSingleItem[] => {
        return [
            { id: 'b7a5db49-6f8f-4323-8d5f-3dbf253e1201', name: 'Ocean Blue', isMain: false },
            { id: 'f1c8cd24-7e31-4ecf-9a5e-2f859a227402', name: 'Emerald Edge', isMain: false },
            { id: 'd2f2f62d-c4b7-4e2b-88f3-f9a4ebfcb003', name: 'Graphite Light', isMain: false },
        ];
    }, []);

    const notifyThemeError = useCallback(
        (error: any, fallbackMessage: string) => {
            const errorMessage = error?.response?.data?.errorMessage;
            toast(errorMessage ? t(`b:${errorMessage}`) : fallbackMessage);
        },
        [t]
    );

    const fetchThemes = useCallback(async () => {
        isThemeListLoadingActions.setFalse();

        try {
            const data = await ThemeService.fetchThemes(companyIdToFilterBy);

            const main = data.find((elem) => elem.isMain);

            main ? setMainThemeId(main.id) : setMainThemeId(DEFAULTTHEME.id);
            main ? setSelectedThemeForSaveAndEdit(main.id) : setSelectedThemeForSaveAndEdit(DEFAULTTHEME.id);

            setThemeList([{ id: DEFAULTTHEME.id, name: DEFAULTTHEME.name, isMain: !main }, ...data]);
        } catch (error: any) {
            const fallbackThemes = isDev ? getDevFallbackThemes() : [];
            setThemeList([{ id: DEFAULTTHEME.id, name: DEFAULTTHEME.name, isMain: true }, ...fallbackThemes]);
            setSelectedThemeForSaveAndEdit(DEFAULTTHEME.id);
            setMainThemeId(DEFAULTTHEME.id);
            if (!isDev) {
                notifyThemeError(error, 'Failed to load themes');
            } else {
                console.warn('[ThemeContext] theme-list request failed. Using development fallback themes.');
            }
        } finally {
            // Prevent infinite spinner on API failure.
            isThemeListLoadingActions.setTrue();
        }
    }, [companyIdToFilterBy, getDevFallbackThemes, isDev, notifyThemeError]);

    const createOrUpdateNewTheme = useCallback(
        async (val: FormikTheme) => {
            isThemeListLoadingActions.setFalse();

            try {
                isEditMode
                    ? await ThemeService.updateTheme(selectedThemeForSaveAndEdit, val)
                    : await ThemeService.createTheme(companyIdToFilterBy, val);
                await fetchThemes();
            } catch (error: any) {
                notifyThemeError(error, 'Failed to save theme');
                isThemeListLoadingActions.setTrue();
            }
        },
        [fetchThemes, selectedThemeForSaveAndEdit, isEditMode, companyIdToFilterBy, notifyThemeError]
    );

    const fetchActivTheme = useCallback(async () => {
        try {
            const route = await ThemeService.fetchCompanyTheme();
            if (route) {
                const params = route.split('/');
                const resp = await ThemeService.fetchBlobTheme(params[params.length - 2], params[params.length - 1]);
                setCurrentTheme(resp);
            } else {
                setCurrentTheme(DEFAULTTHEME);
            }
        } catch (error: any) {
            setCurrentTheme(DEFAULTTHEME);
            notifyThemeError(error, 'Failed to load company theme');
        }
    }, [notifyThemeError]);

    const publishTheme = useCallback(
        async (val: string) => {
            isThemePublishingActions.setTrue();
            try {
                val === DEFAULTTHEME.id
                    ? await ThemeService.restoreToDefault(companyIdToFilterBy)
                    : await ThemeService.publishTheme(val);
                await fetchThemes();
                await fetchActivTheme();
                isThemePublishingActions.setFalse();
            } catch (error: any) {
                notifyThemeError(error, 'Failed to publish theme');
                isThemePublishingActions.setFalse();
            }
        },
        [companyIdToFilterBy, fetchActivTheme, fetchThemes, notifyThemeError, isThemePublishingActions]
    );

    const deleteTheme = useCallback(
        async (val: string) => {
            isThemeListLoadingActions.setFalse();
            try {
                await ThemeService.deleteTheme(val);
                fetchThemes();
            } catch (error: any) {
                notifyThemeError(error, 'Failed to delete theme');
                isThemeListLoadingActions.setTrue();
            }
        },
        [fetchThemes, notifyThemeError]
    );

    const fetchThemesById = useCallback(
        async (val: string) => {
            isThemeListLoadingActions.setFalse();

            try {
                if (val === DEFAULTTHEME.id) {
                    setPreviewTheme({
                        colors: THEME_COLORS,
                        logo: DEFAULTTHEME.logo,
                        lightLogo: DEFAULTTHEME.lightLogo,
                        loader: DEFAULTTHEME.loader,
                    });
                } else {
                    const route = await ThemeService.fetchThemeById(val);
                    if (route) {
                        const params = route.split('/');
                        const resp = await ThemeService.fetchBlobTheme(
                            params[params.length - 2],
                            params[params.length - 1]
                        );

                        setPreviewTheme(resp);
                    } else {
                        setPreviewTheme({
                            colors: THEME_COLORS,
                            logo: DEFAULTTHEME.logo,
                            lightLogo: DEFAULTTHEME.lightLogo,
                            loader: DEFAULTTHEME.loader,
                        });
                    }
                }
            } catch (error: any) {
                notifyThemeError(error, 'Failed to load theme preview');
            } finally {
                isThemeListLoadingActions.setTrue();
            }
        },
        [notifyThemeError]
    );

    const fetchPropertyTheme = useCallback(
        async (propertyId: string) => {
            applayThemeLoaderActions.setTrue();
            try {
                const summary = await PropertyAssetFeatureService.fetchAssetHero(propertyId);

                const data = await ThemeService.fetchThemes(summary.companyOwnerExternalId);

                const main = data.find((elem) => elem.isMain);

                if (main) {
                    const route = await ThemeService.fetchThemeById(main.id);
                    if (route) {
                        const params = route.split('/');
                        const resp = await ThemeService.fetchBlobTheme(
                            params[params.length - 2],
                            params[params.length - 1]
                        );
                        setCurrentTheme(resp);
                    } else {
                        setCurrentTheme(DEFAULTTHEME);
                    }
                } else {
                    setCurrentTheme(DEFAULTTHEME);
                }

                applayThemeLoaderActions.setFalse();
            } catch (error: any) {
                notifyThemeError(error, 'Failed to apply property theme');
                applayThemeLoaderActions.setFalse();
            }
        },
        [applayThemeLoaderActions, notifyThemeError]
    );

    useEffect(() => {
        if (companyIdToFilterBy) {
            fetchThemes();
        }
    }, [companyIdToFilterBy, fetchThemes]);

    useEffect(() => {
        selectedThemeForSaveAndEdit === DEFAULTTHEME.id
            ? setPreviewTheme({
                  colors: THEME_COLORS,
                  logo: DEFAULTTHEME.logo,
                  lightLogo: DEFAULTTHEME.lightLogo,
                  loader: DEFAULTTHEME.loader,
              })
            : fetchThemesById(selectedThemeForSaveAndEdit);
    }, [selectedThemeForSaveAndEdit, fetchThemesById]);

    return (
        <ThemeState.Provider
            value={{
                currentTheme,
                setCurrentTheme,
                previewTheme,
                createOrUpdateNewTheme,
                selectedThemeForSaveAndEdit,
                setSelectedThemeForSaveAndEdit,
                setMainThemeId,
                mainThemeId,
                themeList,
                publishTheme,
                isThemeListLoading,
                isThemeListLoadingActions,
                fetchActivTheme,
                deleteTheme,
                isEditMode,
                isEditModeActions,
                companyIdToFilterBy,
                setCompanyIdToFilterBy,
                fetchPropertyTheme,
            }}
        >
            {children}
            <ModalImportLoading text={t(`f:loaders.publish`)} isModalOpened={isThemePublishing} />
            {applayThemeLoader && <MainLoader type="main" />}
        </ThemeState.Provider>
    );
};

export default ThemeContext;
